import { noop } from "lodash";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Accordion,
  Box,
  IconButton,
  InputBase,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Checkbox from "@mui/material/Checkbox";
import MaterialModal from "../../app/material-modal";
import { BASEStateInterface, checkRole } from "../../../stores/jotai/base";
import { Dispatch, useRef, useState } from "react";
import { formatCategoriesForFavorites } from ".";
import { SetStateAction } from "jotai";
import DeleteOutlinedIcon from "../../../../icons/DeleteOutlinedIcon";
import { ADMIN_VALID_EMAIL } from "../../../constants";

type CategoryManagerModalProps = {
  base: BASEStateInterface;
  customFindingTypes: any[];
  baseUpdateCategory: any;
  baseDeleteCategory: (id: string) => void;
  baseToggleAllFavoritedTags: () => void;
  createCustomTag: (context, inputValue, parentCategory) => Promise<any>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const CategoryManagerModal = (props: CategoryManagerModalProps) => {
  const {
    isOpen,
    base,
    baseDeleteCategory,
    baseToggleAllFavoritedTags,
    baseUpdateCategory,
    createCustomTag,
    customFindingTypes,
    setIsOpen
  } = props;

  const [editing, setEditing] = useState<{
    label: string;
    customFindingTypeID: string;
  }>(null);
  const [creating, setCreating] = useState<{ customFindingTypeID: string }>(
    null
  );
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [popperAnchor, setPopperAnchor] = useState(null);
  const [groupActionsHover, setGroupActionsHover] = useState(null);
  const [nestedActionsHover, setNestedActionsHover] = useState(null);
  const [favoritesExpanded, setFavoritesExpanded] = useState(
    base.favoritedTagsVisibility
  );
  const theme = useTheme();
  const popperOpen = Boolean(popperAnchor);
  const favoritedCategories = formatCategoriesForFavorites(customFindingTypes);
  const modalRef = useRef(null);

  const shouldShowActionItem = (tag) => {
    if (tag.view !== "global") {
      return true;
    } else {
      return checkRole(base, "isSystemAdministrator") && ADMIN_VALID_EMAIL.includes(base.user.email);
    }
  };

  const toggleVisibility = (c) => {
    // A tag can never be favorited and not visible, so always set favorited to false on toggle
    if (c.parentCustomFindingTypeID === "") {
      baseUpdateCategory(c, {
        options: {
          ...c.options,
          visibility: !c.options.visibility,
          expanded: !c.options.visibility
        }
      });

      customFindingTypes.forEach((f) => {
        if (f.parentCustomFindingTypeID === c.customFindingTypeID) {
          baseUpdateCategory(f, {
            options: {
              ...c.options,
              favorited: false,
              visibility: !c.options.visibility
            }
          });
        }
      });
    } else {
      let group = customFindingTypes.find(
        (f) => f.customFindingTypeID === c.parentCustomFindingTypeID
      );
      if (group.options.visibility === false) {
        baseUpdateCategory(group, {
          options: { ...group.options, visibility: true }
        });
      }
      baseUpdateCategory(c, {
        options: {
          ...c.options,
          favorited: false,
          visibility: !c.options.visibility
        }
      });
    }
  };

  const updateInput = (e) => {
    setEditing((category) => {
      let c = { ...category };
      c.label = e.target.value;
      return c;
    });
  };

  const handleTagClick = (tag) => {
    if (shouldShowActionItem(tag)) {
      setEditing(tag);
    } else {
      noop();
    }
  };

  const saveEdit = () => {
    baseUpdateCategory(editing, { label: editing.label });
    setEditing(null);
  };

  const toggleFavorite = (c) => {
    baseUpdateCategory(c, {
      options: {
        ...c.options,
        visibility: true,
        favorited: !c.options.favorited
      }
    });
    let group = customFindingTypes.find(
      (f) => f.customFindingTypeID === c.parentCustomFindingTypeID
    );

    if (group?.options?.visibility === false) {
      baseUpdateCategory(group, {
        options: { ...group.options, visibility: true }
      });
    }
  };

  const toggleAllFavoritedVisibility = (e) => {
    baseToggleAllFavoritedTags();

    setFavoritesExpanded((prev) => {
      if (prev && e.target.checked) {
        // Accordion open & clicked to hide => close accordion
        return false;
      }
      if (!prev && !e.target.checked) {
        // Accordion closed & clicked to show => open accordion
        return true;
      }
      if ((prev && !e.target.checked) || (!prev && e.target.checked)) {
        // Otherwise stays the same
        return prev;
      }
    });
  };

  const createNew = (group) => {
    let inputValue = (document.getElementById(
      creating?.customFindingTypeID
    ) as HTMLInputElement).value;
    createCustomTag("MANAGER", inputValue, group).then((result) => {
      if (result) {
        setCreating(null);
      }
    });
  };

  const createNewGroup = () => {
    let inputValue = (document.getElementById("new-group")  as HTMLInputElement).value;
    createCustomTag("MANAGER", inputValue, "").then((result) => {
      if (result) {
        setCreatingGroup(false);
      }
    });
  };

  const moveCategory = (newGroupID) => {
    baseUpdateCategory(popperAnchor.category, {
      parentCustomFindingTypeID: newGroupID
    });
    setPopperAnchor(null);
  };

  const toggleExpansion = (group) => {
    baseUpdateCategory(group, {
      options: { ...group.options, expanded: !group.options.expanded }
    });
  };

  const deleteTagOrGroup = (findingObject) => {
    if (findingObject.parentCustomFindingTypeID === "") {
      baseDeleteCategory(findingObject.customFindingTypeID);

      findingObject.tags.forEach((t) => {
        baseDeleteCategory(t.customFindingTypeID);
      });
    } else {
      baseDeleteCategory(findingObject.customFindingTypeID);
    }
  };

  const formatCategoriesForManager = (categories) => {
    if (categories === undefined) return [];

    let formattedData = [];
    for (let c of categories) {
      if (c.parentCustomFindingTypeID === "") {
        // formattedData.push({ ...c, tags: [], expanded: false })
        formattedData.push({ ...c, tags: [] });
      }
    }
    for (let g of formattedData) {
      for (let c of categories) {
        if (c.parentCustomFindingTypeID === g.customFindingTypeID) {
          g.tags.push(c);
        }
      }
    }

    return formattedData;
  };

  const renderGroupActionIcons = (g) => {
    if (g.label === "Custom Tags") {
      return (
        <div style={{ padding: "0px 5px" }}>
          <Tooltip title="Add New Tag">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (creating !== g && !g.options.expanded) {
                  toggleExpansion(g);
                  setCreating(g);
                }
                if (creating !== g && g.options.expanded) {
                  setCreating(g);
                }
              }}
              sx={{ padding: "0px 1px" }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={g.options.visibility ? "Hide All Tags" : "Show All Tags"}
          >
            <Checkbox
              icon={<VisibilityIcon sx={{ color: "inherit" }} />}
              checkedIcon={<VisibilityOffIcon sx={{ color: "inherit" }} />}
              checked={g.options.visibility}
              onClick={(e) => {
                e.stopPropagation();
                toggleVisibility(g);
              }}
              sx={{
                padding: "0px 4px",
                color: theme.custom.mainIconColor,
                "&.Mui-checked": { color: theme.custom.mainIconColor },
                ":hover": { color: theme.custom.mainButtonHighlight }
              }}
            />
          </Tooltip>
        </div>
      );
    } else {
      return (
        <div style={{ padding: "0px 5px" }}>
          <Tooltip title="Add New Tag">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (creating !== g && !g.options.expanded) {
                  toggleExpansion(g);
                  setCreating(g);
                }
                if (creating !== g && g.options.expanded) {
                  setCreating(g);
                }
              }}
              sx={{ padding: "0px 1px" }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setEditing(g);
              }}
              sx={{ padding: "0px 1px" }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={g.options.visibility ? "Hide All Tags" : "Show All Tags"}
          >
            <Checkbox
              icon={<VisibilityIcon sx={{ color: "inherit" }} />}
              checkedIcon={<VisibilityOffIcon sx={{ color: "inherit" }} />}
              checked={g.options.visibility}
              onClick={(e) => {
                e.stopPropagation();
                toggleVisibility(g);
              }}
              sx={{
                padding: "0px 4px",
                color: theme.custom.mainIconColor,
                "&.Mui-checked": { color: theme.custom.mainIconColor },
                ":hover": { color: theme.custom.mainButtonHighlight }
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (
                  confirm(
                    "Deleting this Group will delete all Tags within the Group and remove them from any Insights to which they were assigned"
                  )
                ) {
                  deleteTagOrGroup(g);
                }
              }}
              sx={{ padding: "4px 3px 4px 6px" }}
            >
               {/* <IconButton sx={{  padding:'4px 2px 4px 5px' }} onClick={() => this.remove()}> */}
            <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            {/* </IconButton> */}
            </IconButton>
          </Tooltip>
        </div>
      );
    }
  };

  return (
    <MaterialModal
      title="Manage Tags"
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            width: "100%",
            minHeight: "500px",
            minWidth: "780px",
            ".MuiDialogContent-root": {
              overflow: "auto"
            }
          }
        },
        content: {
          backgroundColor: theme.custom.mainBackgroundColor
        }
      }}
      open={isOpen}
      handleDialogClose={setIsOpen}
    >
      <div
        ref={modalRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          marginBottom: "35px"
        }}
      >
        <Accordion
          expanded={favoritesExpanded}
          onMouseEnter={() =>
            setGroupActionsHover({ customFindingTypeID: "favorites" })
          }
          onMouseLeave={() => setGroupActionsHover(null)}
          sx={{
            backgroundColor: base.favoritedTagsVisibility
              ? "default"
              : theme.custom.disabledColor,
            border: base?.favoritedTagsVisibility
              ? `2px solid ${theme.custom.mainButtonHighlight}`
              : `1px solid ${theme.custom.borderGrey}`,
            width: "100%",
            "&.Mui-expanded": { margin: "0px 0px 10px 0px" },
            ":before": { backgroundColor: "transparent" },
            ":last-of-type": { borderRadius: "4px" },
            borderRadius: "4px",
            ":hover": {
              border: base?.favoritedTagsVisibility
                ? `2px solid #125393`
                : `1px solid ${theme.custom.mainButtonHighlight}`
            }
          }}
        >
          <AccordionSummary
            expandIcon={
              <IconButton sx={{ padding: "0px" }}>
                <ArrowRightIcon sx={{ fontSize: "30px" }} />
              </IconButton>
            }
            onClick={() => setFavoritesExpanded((prev) => !prev)}
          >
            {/* <div style={{ display: 'flex', justifyContent: 'space-between' }}> */}
            <Typography
              sx={{
                maxWidth: "75%",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden"
              }}
            >
              Favorites
            </Typography>
            <Tooltip
              title={
                base?.favoritedTagsVisibility
                  ? "Hide All Favorites"
                  : "Show All Favorites"
              }
            >
              <Checkbox
                icon={<VisibilityOffIcon sx={{ color: "inherit" }} />}
                checkedIcon={<VisibilityIcon sx={{ color: "inherit" }} />}
                checked={!base?.favoritedTagsVisibility}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAllFavoritedVisibility(e);
                }}
                sx={{
                  padding: "0px 10px",
                  ":hover": { color: theme.custom.mainButtonHighlight },
                  color: theme.custom.mainIconColor,
                  "&.Mui-checked": { color: theme.custom.mainIconColor }
                }}
              />
            </Tooltip>
            {/* </div> */}
          </AccordionSummary>
          <AccordionDetails sx={{ maxHeight: "240px", overflowY: "auto" }}>
            <List
              sx={{
                paddingTop: "0px",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "5px"
              }}
            >
              {favoritedCategories.map((c) => {
                return (
                  <ListItem
                    key= {c.customFindingTypeID}
                    sx={{ display: "flex", gap: "10px", padding: "0px" }}
                  >
                    <Tooltip
                      title={c.options.favorited ? "Unfavorite" : "Favorite"}
                    >
                      <Checkbox
                        icon={<StarBorderIcon sx={{ color: "inherit" }} />}
                        checkedIcon={<StarIcon sx={{ color: "inherit" }} />}
                        checked={c.options.favorited}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(c);
                        }}
                        sx={{ padding: "0px" }}
                      />
                    </Tooltip>
                    <Box
                      sx={{
                        backgroundColor: c.color || "#fff",
                        borderRadius: "4px",
                        border: "1px solid #dcdcdc",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        padding: "3px 10px",
                        overflow: "hidden",
                        ":hover": {
                          borderColor: theme.custom.mainButtonHighlight
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          maxWidth: "75%",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden"
                        }}
                      >
                        {c.label}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>

        {formatCategoriesForManager(props.customFindingTypes).map((g) => {
          if (g.customFindingTypeID === editing?.customFindingTypeID) {
            // Show input to edit group
            return (
              <ListItem key={g.customFindingTypeID} sx={{ padding: "0px" }}>
                <div
                  style={{
                    backgroundColor: "#fafafa",
                    borderRadius: "4px",
                    border: "1px solid #2372AD",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    alignSelf: "flex-end",
                    padding: "5px 10px",
                    width: "100%",
                    height: "48px"
                  }}
                >
                  <InputBase
                    sx={{ width: "100%" }}
                    value={editing.label}
                    autoFocus
                    onChange={(e) => updateInput(e)}
                    onKeyDown={(e) => (e.key === "Enter" ? saveEdit() : null)}
                  />
                  <div
                    style={{
                      width: "25%",
                      display: "flex",
                      justifyContent: "flex-end"
                    }}
                  >
                    <Tooltip title="Save">
                      <IconButton onClick={saveEdit}>
                        <CheckIcon sx={{ color: "green" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton onClick={() => setEditing(null)}>
                        <CloseIcon sx={{ color: "#d32f2f" }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </ListItem>
            );
          } else {
            // Show regular group accordion
            return (
              <Accordion
                key={g.customFindingTypeID}
                expanded={g.options.expanded}
                onMouseEnter={() => setGroupActionsHover(g)}
                onMouseLeave={() => setGroupActionsHover(null)}
                sx={{
                  width: "100%",
                  "&.Mui-expanded": { margin: "0px 0px 10px 0px" },
                  ":before": { backgroundColor: "transparent" },
                  ":last-of-type": { borderRadius: "4px" },
                  borderRadius: "4px",
                  border: `1px solid ${theme.custom.borderGrey}`,
                  backgroundColor: g.options.visibility
                    ? "default"
                    : theme.custom.disabledColor
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <IconButton sx={{ padding: "0px" }}>
                      <ArrowRightIcon sx={{ fontSize: "30px" }} />
                    </IconButton>
                  }
                  // onClick={() => toggleExpansion(g)}
                  sx={{ justifyContent: "space-between" }}
                >
                  <Typography
                    sx={{
                      maxWidth: "75%",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden"
                    }}
                  >
                    {g.label}
                  </Typography>
                  {groupActionsHover &&
                  groupActionsHover.customFindingTypeID ===
                    g.customFindingTypeID &&
                  shouldShowActionItem(g)
                    ? renderGroupActionIcons(g)
                    : null}
                </AccordionSummary>
                <AccordionDetails
                  sx={{ maxHeight: "240px", overflowY: "auto" }}
                >
                  <List
                    sx={{
                      paddingTop: "0px",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px"
                    }}
                  >
                    {g.tags.map((t) => {
                      if (
                        t.customFindingTypeID === editing?.customFindingTypeID
                      ) {
                        return (
                          <ListItem key={t.customFindingTypeID} sx={{ padding: "0px" }}>
                            <div style={{ width: "35px" }}></div>
                            <div
                              style={{
                                backgroundColor: "#fafafa",
                                borderRadius: "4px",
                                border: "1px solid #2372AD",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                alignSelf: "flex-end",
                                padding: "5px 10px",
                                width: "100%",
                                height: "40px"
                              }}
                            >
                              <InputBase
                                sx={{ width: "100%" }}
                                value={editing.label}
                                autoFocus
                                onChange={(e) => updateInput(e)}
                                onKeyDown={(e) =>
                                  e.key === "Enter" ? saveEdit() : null
                                }
                              />
                              <div
                                style={{
                                  width: "25%",
                                  display: "flex",
                                  justifyContent: "flex-end"
                                }}
                              >
                                <Tooltip title="Save">
                                  <IconButton onClick={saveEdit}>
                                    <CheckIcon sx={{ color: "green" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton onClick={() => setEditing(null)}>
                                    <CloseIcon sx={{ color: "#d32f2f" }} />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                          </ListItem>
                        );
                      } else {
                        return (
                          <ListItem
                            key={t.customFindingTypeID}
                            onMouseOver={() => setNestedActionsHover(t)}
                            onMouseLeave={() => setNestedActionsHover(null)}
                            onClick={() => handleTagClick(t)}
                            sx={{
                              display: "flex",
                              gap: "10px",
                              /*opacity: t.options.visibility ? '100%' : '40%',*/ padding:
                                "0px"
                            }}
                          >
                            <Tooltip
                              title={
                                t.options.favorited ? "Unfavorite" : "Favorite"
                              }
                            >
                              <Checkbox
                                icon={
                                  <StarBorderIcon sx={{ color: "inherit" }} />
                                }
                                checkedIcon={
                                  <StarIcon sx={{ color: "inherit" }} />
                                }
                                checked={t.options.favorited}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(t);
                                }}
                                sx={{
                                  padding: "0px",
                                  opacity: t.options.visibility ? "100%" : "40%"
                                }}
                              />
                            </Tooltip>
                            <Box
                              sx={{
                                backgroundColor: t.options.visibility
                                  ? t.color || "#fff"
                                  : "#dcdcdc",
                                borderRadius: "4px",
                                border: "1px solid lightgrey",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                                padding: "3px 10px",
                                overflow: "hidden",
                                ":hover": {
                                  borderColor: theme.custom.mainButtonHighlight
                                }
                              }}
                            >
                              <Typography
                                sx={{
                                  maxWidth: "75%",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  opacity: t.options.visibility ? "100%" : "40%"
                                }}
                              >
                                {t.label}
                              </Typography>
                              {(nestedActionsHover &&
                                nestedActionsHover.customFindingTypeID ===
                                  t.customFindingTypeID) ||
                              (popperAnchor && popperAnchor.category === t) ? (
                                <div
                                  style={
                                    {
                                      /* display: managerOptionsHover === t.value ? 'block' : 'none' */
                                    }
                                  }
                                >
                                  {popperOpen ? (
                                    <Popover
                                      container={modalRef!.current}
                                      open={popperOpen}
                                      anchorEl={popperAnchor.anchor}
                                      onClose={(e) => {
                                        setPopperAnchor(null);
                                      }}
                                      sx={{ zIndex: theme.custom.menuZIndex }}
                                      anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "center"
                                      }}
                                    >
                                      <div
                                        style={{
                                          backgroundColor: "#FFFFFF",
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                          width: "180px",
                                          borderRadius: "4px",
                                          border: "1px solid lightgrey"
                                        }}
                                      >
                                        <ul
                                          style={{
                                            padding: "4px 0px",
                                            margin: "0px"
                                          }}
                                        >
                                          {customFindingTypes.map((c) => {
                                            if (
                                              c.customFindingTypeID ===
                                                popperAnchor.category
                                                  .parentCustomFindingTypeID ||
                                              c.parentCustomFindingTypeID !== ""
                                            ) {
                                              return;
                                            } else {
                                              return (
                                                <li
                                                  key={c.customFindingTypeID}
                                                  className="submenu-item"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    moveCategory(
                                                      c.customFindingTypeID
                                                    );
                                                  }}
                                                  style={{
                                                    padding: "8px 12px",
                                                    height: "35px",
                                                    overflow: "hidden",
                                                    whiteSpace: "nowrap",
                                                    textOverflow: "ellipsis"
                                                  }}
                                                >
                                                  {c.label}
                                                </li>
                                              );
                                            }
                                          })}
                                        </ul>
                                      </div>
                                    </Popover>
                                  ) : null}

                                  {shouldShowActionItem(t) ? (
                                    <>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={() => setEditing(t)}
                                          sx={{ padding: "0px 1px" }}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Move">
                                        <IconButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPopperAnchor({
                                              anchor: e.target,
                                              category: t
                                            });
                                          }}
                                          sx={{ padding: "0px 1px" }}
                                        >
                                          <DriveFileMoveIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip
                                        title={
                                          t.options.visibility
                                            ? "Hide Tag"
                                            : "Show Tag"
                                        }
                                      >
                                        <Checkbox
                                          icon={
                                            <VisibilityIcon
                                              sx={{ color: "inherit" }}
                                            />
                                          }
                                          checkedIcon={
                                            <VisibilityOffIcon
                                              sx={{ color: "inherit" }}
                                            />
                                          }
                                          checked={t.options.visibility}
                                          // onChange={(e) => toggleVisibility(t, e)}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleVisibility(t);
                                          }}
                                          sx={{
                                            padding: "0px 4px",
                                            "&.Mui-checked": {
                                              color: theme.custom.mainIconColor
                                            },
                                            ":hover": {
                                              color:
                                                theme.custom.mainButtonHighlight
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              confirm(
                                                "Deleting this Tag will remove it from any Insights to which it was assigned"
                                              )
                                            ) {
                                              deleteTagOrGroup(t);
                                            }
                                          }}
                                          sx={{  padding:'4px 3px 4px 6px' }}
                                        >
                                          {/* <IconButton sx={{  padding:'4px 2px 4px 5px' }} onClick={() => this.remove()}> */}
            <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            {/* </IconButton> */}
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : null}
                                </div>
                              ) : null}
                            </Box>
                          </ListItem>
                          //   </div>
                        );
                      }
                    })}
                    <ListItem
                      sx={{
                        padding: "4px 0px 0px 0px",
                        justifyContent: "center"
                      }}
                    >
                      {g.customFindingTypeID ===
                      creating?.customFindingTypeID ? (
                        <div
                          style={{
                            backgroundColor: "#fafafa",
                            borderRadius: "4px",
                            border: "1px solid #2372AD",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            alignSelf: "flex-end",
                            padding: "5px 10px",
                            width: "100%",
                            height: "40px"
                          }}
                        >
                          <InputBase
                            id={creating?.customFindingTypeID}
                            sx={{ width: "100%" }}
                            autoFocus
                            placeholder="New Tag..."
                            onKeyDown={(e) =>
                              e.key === "Enter" ? createNew(g) : null
                            }
                            // onChange={(e) => updateInput(e)}
                          />
                          <div
                            style={{
                              width: "25%",
                              display: "flex",
                              justifyContent: "flex-end"
                            }}
                          >
                            <Tooltip title="Save">
                              <IconButton onClick={() => createNew(g)}>
                                <CheckIcon sx={{ color: "green" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton onClick={(e) => setCreating(null)}>
                                <CloseIcon sx={{ color: "#d32f2f" }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </div>
                      ) : undefined}
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          }
        })}
        {creatingGroup ? (
          <div
            key="creating-group"
            style={{
              backgroundColor: "#fafafa",
              borderRadius: "4px",
              border: "1px solid #2372AD",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              alignSelf: "flex-end",
              padding: "5px 10px",
              width: "100%",
              height: "48px"
            }}
          >
            <InputBase
              id="new-group"
              sx={{ width: "100%" }}
              autoFocus
              placeholder="New Tag Group..."
              onKeyDown={(e) => (e.key === "Enter" ? createNewGroup() : null)}
            />
            <div
              style={{
                width: "25%",
                display: "flex",
                justifyContent: "flex-end"
              }}
            >
              <Tooltip title="Save">
                <IconButton onClick={createNewGroup}>
                  <CheckIcon sx={{ color: "green" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton onClick={() => setCreatingGroup(false)}>
                  <CloseIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ) : undefined}
      </div>
      <Tooltip title="Create New Group" placement="top">
        <IconButton
          disabled={creatingGroup}
          sx={{
            position: "absolute",
            padding: "0px",
            bottom: 7,
            right: 7,
            color: "#2372AD",
            "&:hover": {
              color: "#3683b0"
            }
          }}
          onClick={() => setCreatingGroup(true)}
        >
          <AddCircleIcon
            sx={{ fontSize: "40px", color: theme.custom.mainButtonHighlight }}
          />
        </IconButton>
      </Tooltip>
    </MaterialModal>
  );
};


export default CategoryManagerModal;